/**
 * Shared Storage Utility for Cross-Domain Token Sharing
 * 
 * Compatible with omnix-monitor-core-ui's sharedStorage.ts
 * Uses cookies with Domain=.omnixsystem.com for cross-subdomain sharing
 * 
 * This allows simo-v4 to share backend tokens with new xmonitor modules
 */

const OMNIX_PREFIX = 'omnix_'

const STORAGE_KEYS = {
  BACKEND_TOKEN: 'omnix_backend_token',
  BACKEND_TOKEN_EXPIRES: 'omnix_backend_token_expires',
  USER_CONTEXT: 'omnix_user_context',
}

const isLocalhost = (): boolean => {
  if (typeof window === 'undefined') return false
  const hostname = window.location.hostname
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]'
  )
}

const getCookieDomain = (): string | null => {
  if (typeof window === 'undefined' || isLocalhost()) {
    return null
  }

  const hostname = window.location.hostname

  // For IP addresses, don't set domain
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return null
  }

  // Extract base domain (e.g., omnixsystem.com from app.omnixsystem.com)
  const parts = hostname.split('.')
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join('.')}`
  }

  return null
}

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null

  const nameEQ = encodeURIComponent(name) + '='
  const cookies = document.cookie.split(';')

  for (let cookie of cookies) {
    cookie = cookie.trim()
    if (cookie.indexOf(nameEQ) === 0) {
      const value = cookie.substring(nameEQ.length)
      try {
        return decodeURIComponent(value)
      } catch {
        return value
      }
    }
  }

  return null
}

const setCookie = (name: string, value: string, maxAge: number = 365 * 24 * 60 * 60): void => {
  if (typeof document === 'undefined') return

  const domain = getCookieDomain()
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`
  cookieString += `; Max-Age=${maxAge}`
  
  if (domain) {
    cookieString += `; Domain=${domain}`
  }
  
  cookieString += '; Path=/'
  
  if (!isLocalhost()) {
    cookieString += '; SameSite=None; Secure'
  } else {
    cookieString += '; SameSite=Lax'
  }

  document.cookie = cookieString
}

const removeCookie = (name: string): void => {
  setCookie(name, '', -1)
}

export const sharedStorage = {
  getItem: (key: string): string | null => {
    const prefixedKey = OMNIX_PREFIX + key
    
    if (isLocalhost()) {
      return localStorage.getItem(prefixedKey)
    }
    
    return getCookie(prefixedKey)
  },

  setItem: (key: string, value: string): void => {
    const prefixedKey = OMNIX_PREFIX + key
    
    if (isLocalhost()) {
      localStorage.setItem(prefixedKey, value)
    } else {
      setCookie(prefixedKey, value)
    }
  },

  removeItem: (key: string): void => {
    const prefixedKey = OMNIX_PREFIX + key
    
    if (isLocalhost()) {
      localStorage.removeItem(prefixedKey)
    } else {
      removeCookie(prefixedKey)
    }
  },

  // Convenience methods for backend token
  getBackendToken: (): string | null => {
    const token = sharedStorage.getItem('backend_token')
    const expiresStr = sharedStorage.getItem('backend_token_expires')
    
    if (!token || !expiresStr) return null
    
    const expiresAt = parseInt(expiresStr)
    if (Date.now() > expiresAt) {
      // Token expired, clear it
      sharedStorage.removeItem('backend_token')
      sharedStorage.removeItem('backend_token_expires')
      return null
    }
    
    return token
  },

  setBackendToken: (token: string, expiresInSeconds: number = 86400): void => {
    const expiresAt = Date.now() + (expiresInSeconds * 1000)
    sharedStorage.setItem('backend_token', token)
    sharedStorage.setItem('backend_token_expires', expiresAt.toString())
  },

  clearBackendToken: (): void => {
    sharedStorage.removeItem('backend_token')
    sharedStorage.removeItem('backend_token_expires')
  },

  // User context
  getUserContext: (): string | null => {
    return sharedStorage.getItem('user_context')
  },

  setUserContext: (context: string): void => {
    sharedStorage.setItem('user_context', context)
  },

  clearUserContext: (): void => {
    sharedStorage.removeItem('user_context')
  },

  // Clear all omnix-related storage
  clearAll: (): void => {
    sharedStorage.clearBackendToken()
    sharedStorage.clearUserContext()
    
    // Also clear Auth0 cache
    if (typeof localStorage !== 'undefined') {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('@@auth0spajs@@')) {
          localStorage.removeItem(key)
        }
      })
    }
  },
}

export { STORAGE_KEYS }
